/**
 * Column projections — which fields a given read actually carries.
 *
 * `select('*')` on a trade is ~1,360 B/row: 12.97 MB for a 10k-trade account, of
 * which roughly 90% is annotation prose, screenshot URLs and tag arrays that no
 * metric reads. The fields every `domain/` and `selectors/` function reads come
 * to ~209 B/row. That is the entire case for a projection.
 *
 * ⚠ AND IT IS DANGEROUS, which is why this file is types only and why nothing
 * here can become a `Trade`. A projected row that reaches `tradeToDb` overwrites
 * every column it does not carry — see `io/sync/hydration.ts`. The type below
 * makes that a compile error; the ledger makes it a runtime error. Both, because
 * the compile-time half is defeated by an ordinary object spread.
 *
 * This file lives in `domain/` and imports nothing from `io/`, so the same key
 * sets are usable from a Deno Edge Function.
 */
import type { Trade, TradeColumnKey } from './types';

declare const projection: unique symbol;

/**
 * A row carrying only the keys `K`.
 *
 * The brand is what stops a `Trade` being passed where a projection is expected
 * and — more importantly — stops a projection being passed where a `Trade` is
 * expected, which is the direction that destroys data.
 *
 * ⚠ The brand does NOT survive an object spread. `{ ...BLANK_TRADE, ...row }`
 * type-checks as a `Trade` and TypeScript raises nothing, because it applies no
 * excess-property checking to spreads. That is not a flaw to be fixed here — it
 * is why the runtime hydration ledger exists, and why this type is a first line
 * rather than the line.
 */
export type Projected<K extends TradeColumnKey> = Pick<Trade, K> & {
  readonly [projection]: K;
};

/**
 * THE METRICS SET — every column any `domain/` or `selectors/` function reads.
 *
 * Derived from the call sites, not from a guess: the union of `ValuedTrade`,
 * `ExcursionValuedTrade`, `KpiTrade`, `DatedTrade`, `AccountTrade`,
 * `AccountFilterTrade` and `KpiViewTrade`, verified by reading every non-test
 * file under `domain/` and `selectors/`.
 *
 * `id` is the one member NO domain function reads. It is here because it is the
 * hydration key and the identity of `bestTrade` / `worstTrade`. That exception is
 * stated so the per-column negative control in the gate does not look like a
 * false positive when `id` behaves differently from the other ten.
 *
 * ⚠ NO EXCURSION COLUMN IS HERE, deliberately. `mae_points` / `mfe_points` /
 * `mae_secs` / `mfe_secs` appear nowhere as reads: `excursionValue` takes the
 * magnitude as a PARAMETER, and `computeExcursionWalk` produces them rather than
 * consuming them. The moment a card reads `t.maePoints` this set is wrong — and
 * the gate's positive control is what will say so, rather than a review.
 */
export const METRICS_KEYS = [
  'id',
  'accountId',
  'date',
  'sessionDate',
  'timeIn',
  'contract',
  'qty',
  'points',
  'netPnl',
  'riskPoints',
  'rrRealized',
] as const satisfies readonly TradeColumnKey[];

export type MetricsKey = (typeof METRICS_KEYS)[number];
export type MetricsRow = Projected<MetricsKey>;

/**
 * ⚠ THERE IS DELIBERATELY NO `LIST_KEYS` YET.
 *
 * A list projection is the obvious next set — metrics plus whatever the trades
 * table draws. It is not defined here because **this repo has no trades table**:
 * `ui/` is Phase 4. Deriving it from the OLD client's `renderTradeTable` would be
 * reading a 48k-line file's structure and calling the result verified, which is
 * the exact move [D21] forbids and which already let `computeKpis` go unported
 * under a green Phase 1 gate.
 *
 * What such a set would need to cover, recorded so the work is not re-derived:
 * the Disc.% column reads the discipline `booleans`; the Unmerge action reads
 * `mergedFrom`, which rides `extras`; Import History's batch review reads
 * `extras.importBatchId`; the "missing notes" facet needs a `has_notes` boolean
 * rather than the prose. Three of those four live in `extras`, so a list
 * projection is not simply a longer column list — it needs a JSON-path select or
 * a generated column, and that is a schema decision (D6, additive only).
 *
 * Define it when the table exists and the gate can measure it.
 */
